import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef } from "ag-grid-community";
import { useNavigate, useParams } from "react-router-dom";
import {
  LoadingState,
  InputNumber,
  AgGridTable,
  SearchableSelect,
} from "components";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { useCustomer, usePurchaseOrder, useWmsItem } from "hooks";
import {
  PurchaseOrderForm,
  PurchaseOrderItem,
  PurchaseOrder,
  Customer,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";
import { Controller, useForm } from "react-hook-form";
import {
  normalizeDateInput,
  parseNumberSafe as normalizeNumber,
  formatCurrency,
} from "utils/helpers/accounting";
import { sanitizePurchaseOrderItems } from "utils/helpers/sanitizers";

const todayString = () => new Date().toISOString().slice(0, 10);
const ATTACHMENT_SPLIT_REGEX = /\n/;

type PurchaseOrderItemWithMeta = PurchaseOrderItem & {
  id?: number | string;
  __tempId?: string;
  unit_label?: string;
};

const getUnitString = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "object") {
    return value.name ?? value.code ?? "";
  }
  return "";
};

const defaultItem: PurchaseOrderItemWithMeta = {
  product_id: "",
  product_name: "",
  unit_id: "",
  unit_label: "",
  quantity: 1,
  unit_price: 0,
  vat_rate: 0,
  description: "",
};

const defaultFormValues: PurchaseOrderForm = {
  purchase_date: todayString(),
  purchase_code: "",
  supplier_id: "",
  supplier_name: "",
  address: "",
  description: "",
  attachments: [],
  items: [],
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch (error) {
      // ignore invalid JSON strings
    }

    return trimmed
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const getItemAmount = (item: PurchaseOrderItem): number => {
  const quantity = normalizeNumber(item.quantity);
  const unitPrice = normalizeNumber(item.unit_price);
  if ((item as any).amount !== undefined && item.amount !== null) {
    return normalizeNumber((item as any).amount);
  }
  return quantity * unitPrice;
};

const getItemVatAmount = (item: PurchaseOrderItem): number => {
  const amount = getItemAmount(item);
  const vatRate =
    item.vat_rate !== undefined && item.vat_rate !== null
      ? Number(item.vat_rate)
      : 0;
  if ((item as any).vat_amount !== undefined && item.vat_amount !== null) {
    return normalizeNumber((item as any).vat_amount);
  }
  return Number.isFinite(vatRate) && vatRate > 0 ? (amount * vatRate) / 100 : 0;
};

const ensureTempIds = (
  list: PurchaseOrderItemWithMeta[],
): PurchaseOrderItemWithMeta[] =>
  list.map((item) => {
    if (item.__tempId) return item;
    return {
      ...item,
      __tempId: `temp-${Math.random().toString(36).slice(2, 9)}`,
    };
  });

const PurchaseOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { fetchDetail, createPurchaseOrder, updatePurchaseOrder } =
    usePurchaseOrder();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const { data: detailResponse, isFetching: isFetchingDetail } = fetchDetail(
    id ?? "",
    isEdit,
  );
  const { fetchList: fetchWmsItems } = useWmsItem();
  const { data: wmsItems = [] } = fetchWmsItems(true);

  const supplierOptions =
    (customersData?.data as any[])?.map((c) => ({
      value: c.id,
      label: `${c.code}${c.name ? ` - ${c.name}` : ""}`,
      address: c.address,
      name: c.name,
    })) ?? [];

  const wmsItemOptions = useMemo(
    () =>
      (wmsItems ?? []).map((item: any) => ({
        value: item.id,
        label: `${item.code}${item.name ? ` - ${item.name}` : ""}`,
        name: item.name,
        unit_id: item.unit_id,
        unit: getUnitString(item.unit),
      })),
    [wmsItems],
  );

  const wmsItemById = useMemo(() => {
    const map = new Map<number | string, any>();
    (wmsItems ?? []).forEach((item: any) => {
      map.set(item.id, item);
      map.set(String(item.id), item);
      if (item.wms_id !== undefined && item.wms_id !== null) {
        map.set(item.wms_id, item);
        map.set(String(item.wms_id), item);
      }
    });
    return map;
  }, [wmsItems]);

  const attachProductInfo = useCallback(
    (list: PurchaseOrderItemWithMeta[]) =>
      list.map((item) => {
        const selected =
          wmsItemById.get(item.product_id as any) ||
          wmsItemById.get(item.unit_id as any) ||
          wmsItemById.get(String(item.product_id ?? "")) ||
          wmsItemById.get(String(item.unit_id ?? ""));
        if (!selected) return item;
        return {
          ...item,
          product_name: item.product_name || selected.name || "",
          unit_id:
            item.unit_id ??
            selected.unit_id ??
            (selected.unit ? selected.unit.id : undefined),
          unit_label:
            item.unit_label ||
            getUnitString((selected as any)?.unit) ||
            (selected.unit_id ? String(selected.unit_id) : undefined),
        };
      }),
    [wmsItemById],
  );

  const {
    control,
    register,
    handleSubmit: formHandleSubmit,
    reset,
    setValue,
    getValues,
  } = useForm<PurchaseOrderForm>({
    defaultValues: defaultFormValues,
  });
  const itemIdRef = useRef(0);
  const [items, setItems] = useState<PurchaseOrderItemWithMeta[]>(() =>
    ensureTempIds([defaultItem]),
  );

  useEffect(() => {
    if (!detailResponse?.data || !customersData?.data) return;
    const order = detailResponse.data as PurchaseOrder;
    const suppliers = customersData?.data as Customer[];
    const supplier =
      suppliers?.find((s) => s.id === order?.supplier_id) ?? null;
    const normalizedAttachments = normalizeAttachments(order.attachments);
    const sanitizedItems = attachProductInfo(
      ensureTempIds(
        sanitizePurchaseOrderItems(
          order.items,
          wmsItemById,
        ) as PurchaseOrderItemWithMeta[],
      ),
    );
    const purchaseDate =
      normalizeDateInput(order.purchase_date) || todayString();

    reset({
      purchase_date: purchaseDate,
      purchase_code: order.purchase_code || "",
      supplier_id: order.supplier_id ?? "",
      supplier_name: supplier?.name ?? "",
      address: supplier?.address ?? "",
      description: order.description ?? "",
      attachments: normalizedAttachments,
      items: sanitizedItems,
    });
    setItems(
      sanitizedItems.length ? sanitizedItems : ensureTempIds([defaultItem]),
    );
  }, [
    attachProductInfo,
    detailResponse,
    reset,
    wmsItemById,
    customersData?.data,
  ]);

  useEffect(() => {
    if (isEdit) return;
    reset(defaultFormValues);
    setItems(ensureTempIds([defaultItem]));
  }, [isEdit, reset]);

  useEffect(() => {
    if (!isEdit) return;
    setItems((prev) => attachProductInfo(prev));
  }, [attachProductInfo, isEdit]);

  const totalAmount = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + getItemAmount(item) + getItemVatAmount(item),
        0,
      ),
    [items],
  );

  const handleAddItem = useCallback(() => {
    setItems((prev) => {
      const nextId = `temp-${itemIdRef.current++}`;
      return [...prev, { ...defaultItem, __tempId: nextId }];
    });
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof PurchaseOrderItemWithMeta, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                [key]:
                  key === "quantity" ||
                  key === "unit_price" ||
                  key === "vat_rate"
                    ? normalizeNumber(value)
                    : key === "product_id" || key === "unit_id"
                      ? Number.isFinite(Number(value))
                        ? Number(value)
                        : value
                      : value,
              }
            : item,
        ),
      );
    },
    [],
  );

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleSelectProduct = useCallback(
    (index: number, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) => {
          if (idx !== index) return item;
          if (!value) {
            return {
              ...item,
              product_id: "",
              product_name: "",
              unit_id: "",
              unit_label: "",
            };
          }
          const selected =
            wmsItemById.get(Number(value)) ||
            wmsItemById.get(String(value)) ||
            wmsItemById.get((value as any) ?? "");
          return {
            ...item,
            product_id: Number.isFinite(Number(value)) ? Number(value) : value,
            product_name: selected?.name ?? "",
            unit_id:
              selected?.unit_id !== undefined && selected?.unit_id !== null
                ? selected.unit_id
                : item.unit_id,
            unit_label:
              getUnitString((selected as any)?.unit) || item.unit_label || "",
          };
        }),
      );
    },
    [wmsItemById],
  );

  const suppressGridKeyboard = useCallback((params: any) => {
    const event = params?.event as KeyboardEvent | undefined;
    if (!event) return false;
    const target = event.target as HTMLElement | null;
    if (!target) return false;
    const tag = target.tagName;
    if (
      (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") &&
      ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    ) {
      event.stopPropagation?.();
      return true;
    }
    return false;
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      floatingFilter: false,
      resizable: true,
      editable: false,
      minWidth: 120,
      cellClass: "!border-transparent",
      suppressKeyboardEvent: suppressGridKeyboard,
    }),
    [suppressGridKeyboard],
  );

  const columns = useMemo<
    ColDef<PurchaseOrderItemWithMeta & { __actions: any }>[]
  >(() => {
    const renderNumberInput = (
      params: any,
      key: keyof PurchaseOrderItem,
      opts?: { suffix?: string; decimalScale?: number },
    ) => {
      const rowIndex = params.node?.rowIndex ?? 0;
      return (
        <InputNumber
          value={normalizeNumber((params.data as PurchaseOrderItem)?.[key])}
          onValueChange={(values) =>
            handleItemChange(rowIndex, key, values.floatValue ?? 0)
          }
          className="h-9 w-full px-2 py-1 text-right text-sm"
          suffix={opts?.suffix}
          decimalScale={opts?.decimalScale ?? 0}
        />
      );
    };

    return [
      {
        headerName: "Hàng hóa",
        field: "product_id",
        minWidth: 200,
        flex: 1.1,
        editable: false,
        pinned: "left",
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <select
              value={(params.data as PurchaseOrderItem)?.product_id ?? ""}
              onChange={(e) => handleSelectProduct(rowIndex, e.target.value)}
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Chọn hàng hóa</option>
              {wmsItemOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        headerName: "Tên hàng",
        field: "product_name",
        minWidth: 200,
        flex: 1,
        editable: false,
        cellRenderer: (params) => (
          <span className="text-gray-900">
            {(params.data as PurchaseOrderItem)?.product_name ?? ""}
          </span>
        ),
      },
      {
        headerName: "Đơn vị",
        field: "unit_label",
        minWidth: 140,
        flex: 0.8,
        editable: false,
        cellRenderer: (params) => (
          <span className="text-gray-900">
            {(params.data as any)?.unit_label ?? ""}
          </span>
        ),
      },
      {
        headerName: "SL",
        field: "quantity",
        minWidth: 140,
        flex: 0.8,
        cellRenderer: (params) =>
          renderNumberInput(params, "quantity", { decimalScale: 2 }),
      },
      {
        headerName: "Đơn giá",
        field: "unit_price",
        minWidth: 160,
        flex: 0.9,
        cellRenderer: (params) =>
          renderNumberInput(params, "unit_price", {
            decimalScale: 2,
            suffix: " ₫",
          }),
      },
      {
        headerName: "VAT %",
        field: "vat_rate",
        minWidth: 140,
        flex: 0.8,
        cellRenderer: (params) =>
          renderNumberInput(params, "vat_rate", {
            decimalScale: 2,
            suffix: "%",
          }),
      },
      {
        headerName: "Thành tiền",
        field: "amount",
        minWidth: 150,
        flex: 1,
        editable: false,
        valueGetter: (params) =>
          getItemAmount(params.data as PurchaseOrderItem),
        valueFormatter: (params) =>
          formatCurrency(normalizeNumber(params.value)),
        cellClass: "text-right text-gray-900 font-semibold",
      },
      {
        headerName: "Thuế VAT",
        field: "vat_amount",
        minWidth: 150,
        flex: 1,
        editable: false,
        valueGetter: (params) =>
          getItemVatAmount(params.data as PurchaseOrderItem),
        valueFormatter: (params) =>
          formatCurrency(normalizeNumber(params.value)),
        cellClass: "text-right text-gray-900 font-semibold",
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 200,
        flex: 1,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <input
              value={(params.data as PurchaseOrderItem)?.description ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "description", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Ghi chú"
            />
          );
        },
      },
      {
        headerName: "Thao tác",
        field: "__actions",
        width: 130,
        editable: false,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <div className="flex items-center justify-center h-full">
              <button
                type="button"
                onClick={() => handleRemoveItem(rowIndex)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-60"
                disabled={items.length === 1}
              >
                <Trash2 className="h-4 w-4" />
                Xóa
              </button>
            </div>
          );
        },
      },
    ];
  }, [
    handleItemChange,
    handleRemoveItem,
    handleSelectProduct,
    items.length,
    wmsItemOptions,
  ]);

  const getItemRowId = useCallback((params: any) => {
    const data = params.data as PurchaseOrderItemWithMeta;
    if (data?.__tempId) return data.__tempId;
    return String(params.rowIndex ?? params.node?.rowIndex ?? Math.random());
  }, []);

  const buildPayload = useCallback(
    (data: PurchaseOrderForm): PurchaseOrderForm | null => {
      if (!data.purchase_date || !data.purchase_code || !data.supplier_id) {
        SnackbarUtils.error(
          "Ngày chứng từ, Số chứng từ và Nhà cung cấp là bắt buộc",
        );
        return null;
      }

      const normalizedItems = sanitizePurchaseOrderItems(
        items,
        wmsItemById,
      ) as PurchaseOrderItemWithMeta[];
      if (
        !normalizedItems.length ||
        normalizeNumber(normalizedItems[0].quantity) <= 0 ||
        normalizeNumber(normalizedItems[0].unit_price) <= 0
      ) {
        SnackbarUtils.error(
          "Vui lòng nhập ít nhất một dòng với số lượng và đơn giá",
        );
        return null;
      }

      const attachments = Array.isArray(data.attachments)
        ? normalizeAttachments(data.attachments)
        : normalizeAttachments(data.attachments ?? "");

      return {
        ...data,
        attachments,
        items: normalizedItems.map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          product_id: item.product_id,
          product_name: item.product_name,
          unit_id: item.unit_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount,
          vat_rate: item.vat_rate,
          vat_amount: item.vat_amount,
          description: item.description?.trim() || undefined,
        })),
        order_date: data.purchase_date,
        order_code: data.purchase_code,
      };
    },
    [items, wmsItemById],
  );

  const onSubmit = useCallback(
    (data: PurchaseOrderForm) => {
      const payload = buildPayload(data);
      if (!payload) return;

      if (isEdit && id) {
        updatePurchaseOrder.mutate(
          { id, ...payload },
          {
            onSuccess: () => {
              SnackbarUtils.success("Cập nhật chứng từ mua hàng thành công");
              navigate(ROUTES.ACCOUNTING.PURCHASE_DOCUMENT);
            },
          },
        );
        return;
      }

      createPurchaseOrder.mutate(payload, {
        onSuccess: () => {
          SnackbarUtils.success("Tạo chứng từ mua hàng thành công");
          navigate(ROUTES.ACCOUNTING.PURCHASE_DOCUMENT);
        },
      });
    },
    [
      buildPayload,
      createPurchaseOrder,
      id,
      isEdit,
      navigate,
      updatePurchaseOrder,
    ],
  );

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();

      // Explicit validation before form submission
      const formData = getValues();
      if (
        !formData.purchase_date ||
        !formData.purchase_code ||
        !formData.supplier_id
      ) {
        SnackbarUtils.error(
          "Ngày chứng từ, Số chứng từ và Nhà cung cấp là bắt buộc",
        );
        return;
      }

      // Call react-hook-form's handleSubmit
      formHandleSubmit(onSubmit)(e);
    },
    [formHandleSubmit, getValues, onSubmit],
  );

  const isSaving =
    createPurchaseOrder.isPending || updatePurchaseOrder.isPending;

  // Keyboard shortcuts: Ctrl+S save, Ctrl+N add item
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (!isSaving && !isFetchingDetail) {
          handleSubmit();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "n") {
        e.preventDefault();
        if (!isSaving && !isFetchingDetail) {
          handleAddItem();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleSubmit, handleAddItem, isSaving, isFetchingDetail]);

  return (
    <>
      {(isFetchingDetail || isSaving) && (
        <LoadingState
          message={
            isEdit ? "Đang tải chứng từ mua hàng" : "Đang lưu chứng từ mua hàng"
          }
          description="Vui lòng chờ trong giây lát."
        />
      )}
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 uppercase">Chứng từ mua hàng</p>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEdit ? "Cập nhật chứng từ" : "Thêm chứng từ"}
            </h1>
            <p className="text-sm text-gray-600">
              Nhập thông tin chung và danh sách hàng hóa.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ACCOUNTING.PURCHASE_DOCUMENT)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Danh sách chứng từ
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Đang lưu..." : "Lưu chứng từ"}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Thông tin chung
              </h2>
            </div>
            <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Số chứng từ *
                </label>
                <input
                  {...register("purchase_code", { required: true })}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="PO000001"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Ngày chứng từ *
                </label>
                <input
                  type="date"
                  {...register("purchase_date", { required: true })}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Nhà cung cấp *
                </label>
                <Controller
                  control={control}
                  name="supplier_id"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <SearchableSelect
                      options={supplierOptions.map((opt) => ({
                        value: opt.value,
                        label: opt.label,
                        searchData: opt.name,
                      }))}
                      value={field.value}
                      onChange={(value) => {
                        const selectedValue = value ? Number(value) : "";
                        field.onChange(selectedValue);
                        const selected = supplierOptions.find(
                          (opt) => String(opt.value) === String(value),
                        );
                        if (selected) {
                          setValue("supplier_name", selected.name ?? "", {
                            shouldDirty: true,
                          });
                          if (!getValues("address") && selected.address) {
                            setValue("address", selected.address, {
                              shouldDirty: true,
                            });
                          }
                        }
                      }}
                      placeholder="Tìm kiếm nhà cung cấp..."
                      clearable
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Tên NCC
                </label>
                <input
                  {...register("supplier_name")}
                  readOnly
                  className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                  placeholder="Công ty ABC"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Địa chỉ
                </label>
                <input
                  {...register("address")}
                  readOnly
                  className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                  placeholder="Hà Nội"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Đính kèm (nhiều tệp)
                </label>
                <Controller
                  control={control}
                  name="attachments"
                  render={({ field }) => (
                    <textarea
                      value={
                        Array.isArray(field.value) ? field.value.join("\n") : ""
                      }
                      onChange={(e) =>
                        field.onChange(
                          e.target.value
                            .split(ATTACHMENT_SPLIT_REGEX)
                            .map((item) => item.trim())
                            .filter(Boolean),
                        )
                      }
                      placeholder="file1.pdf, file2.jpg"
                      rows={2}
                      className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    />
                  )}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Diễn giải
                </label>
                <textarea
                  {...register("description")}
                  rows={2}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Nội dung mua hàng"
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Danh sách hàng hóa
              </h2>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Tạm tính</p>
                  <p className="text-base font-semibold text-gray-900">
                    {totalAmount.toLocaleString("vi-VN")} đ
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                  Thêm dòng
                </button>
              </div>
            </div>

            <div className="px-2 py-3">
              <AgGridTable
                rows={items}
                columns={columns}
                pagination={false}
                defaultColDef={defaultColDef}
                pinnedLeftKeys={["product_id"]}
                immutableData
                deltaRowDataMode
                getRowId={getItemRowId}
              />
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm text-gray-500">
                Tạm tính:{" "}
                <span className="font-semibold text-gray-900">
                  {totalAmount.toLocaleString("vi-VN")} đ
                </span>
              </p>
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
                disabled={isSaving}
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Đang lưu..." : "Lưu chứng từ"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default PurchaseOrderDetail;
